import { Head, Link } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
  status: number
}

interface GpsAssignment {
  id: number
  gps_device_name: string | null
  installed_at: string | null
  installed_by: string | null
  install_vehicle: string | null
  install_gps_location: string | null
  install_vehicle_location: string | null
  removed_at: string | null
  removed_by: string | null
  remove_vehicle_location: string | null
  status: number
  active: boolean
}

interface Summary {
  total: number
  active: number
  removed: number
}

interface Props {
  case_record: CaseRecord
  assignments: GpsAssignment[]
  summary: Summary
  can_create: boolean
  can_update: boolean
}

function GpsCard({
  assignment,
  caseId,
  canUpdate,
}: {
  assignment: GpsAssignment
  caseId: number
  canUpdate: boolean
}) {
  return (
    <li className="border-hpi-light-gray border-b py-3 last:border-b-0">
      <div className="flex items-start justify-between">
        <div>
          <span className="font-semibold">{assignment.gps_device_name}</span>{" "}
          <span className="text-hpi-dark-gray">
            Installed on {assignment.installed_at ?? "—"} by{" "}
            {assignment.installed_by ?? "—"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {assignment.active ? (
            <>
              <span className="bg-hpi-dark-green/20 text-hpi-dark-green rounded px-2 py-1 text-xs">
                Active
              </span>
              {canUpdate && (
                <>
                  <Link
                    href={`/cases/${caseId}/gps/${assignment.id}/edit`}
                    className="border-hpi-active-blue text-hpi-active-blue hover:bg-hpi-active-blue rounded border px-2 py-1 text-xs hover:text-white"
                  >
                    Edit
                  </Link>
                  <Link
                    href={`/cases/${caseId}/gps/${assignment.id}/remove`}
                    className="border-hpi-red text-hpi-red hover:bg-hpi-red rounded border px-2 py-1 text-xs hover:text-white"
                  >
                    Remove
                  </Link>
                </>
              )}
            </>
          ) : (
            <>
              <span className="bg-hpi-pale-gray text-hpi-dark-gray rounded px-2 py-1 text-xs">
                Removed
              </span>
              {canUpdate && (
                <Link
                  href={`/cases/${caseId}/gps/${assignment.id}/edit`}
                  className="border-hpi-active-blue text-hpi-active-blue hover:bg-hpi-active-blue rounded border px-2 py-1 text-xs hover:text-white"
                >
                  Edit
                </Link>
              )}
            </>
          )}
        </div>
      </div>
      <div className="text-hpi-dark-gray mt-1 space-y-0.5 text-sm">
        {assignment.install_vehicle && (
          <div>
            <strong>Vehicle:</strong> {assignment.install_vehicle}
          </div>
        )}
        {assignment.install_gps_location && (
          <div>
            <strong>GPS Position on Vehicle:</strong>{" "}
            {assignment.install_gps_location}
          </div>
        )}
        {assignment.install_vehicle_location && (
          <div>
            <strong>Location of Vehicle when GPS installed:</strong>{" "}
            {assignment.install_vehicle_location}
          </div>
        )}
        {assignment.removed_at && (
          <div className="mt-2">
            <strong>Removed:</strong> {assignment.removed_at}
            {assignment.removed_by && <span> by {assignment.removed_by}</span>}
          </div>
        )}
        {assignment.remove_vehicle_location && (
          <div>
            <strong>Location of Vehicle when GPS removed:</strong>{" "}
            {assignment.remove_vehicle_location}
          </div>
        )}
      </div>
    </li>
  )
}

export default function Index({
  case_record,
  assignments,
  summary,
  can_create,
  can_update,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "GPS", href: `/cases/${case_record.id}/gps` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`GPS - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            GPS Entries for {case_record.case_number}
          </h1>
          {can_create && (
            <Link
              href={`/cases/${case_record.id}/gps/new`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white"
            >
              Install GPS
            </Link>
          )}
        </div>

        {/* Summary Cards */}
        <div className="mb-6 grid grid-cols-3 gap-4">
          <div className="rounded-lg bg-white p-4 shadow">
            <div className="text-hpi-med-gray text-sm">Total</div>
            <div className="text-2xl font-bold">{summary.total}</div>
          </div>
          <div className="rounded-lg bg-white p-4 shadow">
            <div className="text-hpi-med-gray text-sm">Active</div>
            <div className="text-2xl font-bold">{summary.active}</div>
          </div>
          <div className="rounded-lg bg-white p-4 shadow">
            <div className="text-hpi-med-gray text-sm">Removed</div>
            <div className="text-2xl font-bold">{summary.removed}</div>
          </div>
        </div>

        {assignments.length > 0 ? (
          <div className="rounded-lg bg-white shadow">
            <h3 className="border-hpi-light-gray border-b px-4 py-3 text-center text-lg font-semibold">
              GPS Entries
            </h3>
            <ul className="divide-hpi-light-gray divide-y px-4">
              {assignments.map((assignment) => (
                <GpsCard
                  key={assignment.id}
                  assignment={assignment}
                  caseId={case_record.id}
                  canUpdate={can_update}
                />
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-center">
            <h3 className="mb-4 text-lg font-semibold">GPS Units</h3>
            <p className="text-hpi-med-gray">No GPS Units found</p>
          </div>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
